import { useState, useEffect } from 'react';
import Navigation from './components/Navigation';
import HeroSection from './components/HeroSection';
import PricingSection from './components/PricingSection';
import Footer from '../home/components/Footer';
import SchemaOrg from '../../components/feature/SchemaOrg';

interface FormData {
  formule: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  adresse: string;
  ville: string;
  codePostal: string;
  typePac: string;
  marquePac: string;
  anneePac: string;
  commentaires: string;
}

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

const contratSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      '@id': `${siteUrl}/contrat-entretien#service`,
      name: "Contrat d'entretien pompe à chaleur - AIRPURE",
      provider: {
        '@type': 'Organization',
        name: 'AIRPURE',
        url: siteUrl,
        telephone: '+33189293323',
      },
      description:
        "Contrats d'entretien pour pompes à chaleur air/air et air/eau. Maintenance préventive et corrective pour optimiser les performances, prolonger la durée de vie et garantir les économies d'énergie.",
      areaServed: { '@type': 'Country', name: 'France' },
      url: `${siteUrl}/contrat-entretien`,
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: "Formules de contrat d'entretien PAC",
        itemListElement: [
          {
            '@type': 'Offer',
            name: "Contrat Essentiel AIRPURE",
            itemOffered: {
              '@type': 'Service',
              name: "Contrat Essentiel - Entretien annuel pompe à chaleur",
              description: "Visite annuelle de maintenance préventive, nettoyage des filtres et vérification du système.",
            },
          },
          {
            '@type': 'Offer',
            name: "Contrat Confort AIRPURE",
            itemOffered: {
              '@type': 'Service',
              name: "Contrat Confort - Entretien pompe à chaleur avec dépannage",
              description: "Visite annuelle + hotline dédiée + 1 dépannage offert par an.",
            },
          },
          {
            '@type': 'Offer',
            name: "Contrat Premium AIRPURE",
            itemOffered: {
              '@type': 'Service',
              name: "Contrat Premium - Entretien pompe à chaleur tout inclus",
              description: "Visite annuelle + dépannages illimités + pièces de rechange couvertes + priorité d'intervention 24h.",
            },
          },
        ],
      },
    },
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/contrat-entretien#webpage`,
      url: `${siteUrl}/contrat-entretien`,
      name: "Contrat d'entretien pompe à chaleur AIRPURE | Maintenance PAC France",
      description:
        "Souscrivez un contrat d'entretien AIRPURE pour votre pompe à chaleur. Maintenance préventive, dépannage prioritaire et économies d'énergie garanties partout en France.",
      inLanguage: 'fr-FR',
      dateModified: '2026-04-12',
      isPartOf: { '@type': 'WebSite', name: 'AIRPURE', url: siteUrl },
      about: { '@id': `${siteUrl}/contrat-entretien#service` },
    },
    {
      '@type': 'FAQPage',
      '@id': `${siteUrl}/contrat-entretien#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: "Pourquoi souscrire un contrat d'entretien pour ma pompe à chaleur ?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Un entretien annuel de votre pompe à chaleur permet de maintenir un COP optimal, de réduire votre consommation jusqu'à 30 %, de conserver la garantie constructeur active et de prévenir les pannes coûteuses.",
          },
        },
        {
          '@type': 'Question',
          name: "Combien coûte un contrat d'entretien AIRPURE ?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: "AIRPURE propose trois formules adaptées à vos besoins : Essentiel (maintenance annuelle), Confort (avec dépannage inclus) et Premium (tout inclus avec pièces et priorité d'intervention). Contactez-nous au 01 89 29 33 23 pour un devis personnalisé.",
          },
        },
        {
          '@type': 'Question',
          name: "Combien de temps dure une intervention d'entretien ?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Une visite d'entretien standard dure entre 1h30 et 2h selon le type et la taille de votre pompe à chaleur. Nos techniciens certifiés interviennent partout en France.",
          },
        },
      ],
    },
  ],
};

const ContratEntretienPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [selectedFormula, setSelectedFormula] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [formData, setFormData] = useState<FormData>({
    formule: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    codePostal: '',
    typePac: '',
    marquePac: '',
    anneePac: '',
    commentaires: ''
  });

  const handleSelectFormula = (formula: string) => {
    setSelectedFormula(formula);
    setFormData(prev => ({ ...prev, formule: formula }));
    setShowForm(true);
    setSubmitStatus(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3n29fct07omtp5lraa0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSubmit as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          formule: '',
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          adresse: '',
          ville: '',
          codePostal: '',
          typePac: '',
          marquePac: '',
          anneePac: '',
          commentaires: ''
        });
        setTimeout(() => {
          setShowForm(false);
          setSubmitStatus(null);
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    document.title =
      "Contrat d'entretien Pompe à Chaleur AIRPURE | Maintenance PAC & Économies France";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        "Souscrivez un contrat d'entretien AIRPURE pour votre pompe à chaleur. Maintenance préventive, dépannage prioritaire et économies d'énergie jusqu'à 30 % garanties partout en France.",
      );
    }
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "contrat entretien pompe à chaleur,maintenance PAC,dépannage PAC,économies énergie,AIRPURE");
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${siteUrl}/contrat-entretien`);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SchemaOrg schema={contratSchema} id="schema-contrat-entretien" />
      <Navigation />
      <HeroSection />
      <PricingSection onSelectFormula={handleSelectFormula} />
      
      {/* Section Avantages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <span className="inline-block bg-emerald-100 text-emerald-600 text-sm font-bold px-4 py-1.5 rounded-full mb-4">Pourquoi souscrire ?</span>
            <h2 className="text-4xl font-extrabold text-slate-900 mb-4">
              Votre PAC mérite le meilleur entretien
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Un entretien annuel, c'est jusqu'à 30 % d'économies supplémentaires et une durée de vie doublée.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-7">
            {[
              { icon: 'ri-shield-check-line', color: 'bg-orange-100 text-orange-500', title: 'Garantie constructeur maintenue', desc: 'Maintenez la garantie constructeur active et protégez la valeur de votre équipement sur le long terme.' },
              { icon: 'ri-leaf-line', color: 'bg-emerald-100 text-emerald-600', title: 'Économies d\'énergie optimisées', desc: 'Un entretien régulier maintient le COP de votre PAC au maximum et réduit votre consommation jusqu\'à 30 %.' },
              { icon: 'ri-customer-service-2-line', color: 'bg-teal-100 text-teal-600', title: 'Priorité d\'intervention', desc: 'Nos clients sous contrat bénéficient d\'une hotline dédiée et d\'une intervention prioritaire sous 24h à 72h.' },
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-8 flex flex-col items-center text-center">
                <div className={`w-16 h-16 ${item.color} rounded-2xl flex items-center justify-center mb-5`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-extrabold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulaire de souscription */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Souscription - {selectedFormula}
                </h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl">
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-xl text-emerald-500"></i>
                    Votre demande a été envoyée avec succès ! Nous vous contacterons sous 24h.
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-xl mr-2"></i>
                    Une erreur s'est produite. Veuillez réessayer.
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form id="contrat-entretien-form">
                <input type="hidden" name="formule" value={formData.formule} />
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse *
                  </label>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ville *
                    </label>
                    <input
                      type="text"
                      name="ville"
                      value={formData.ville}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Code postal *
                    </label>
                    <input
                      type="text"
                      name="codePostal"
                      value={formData.codePostal}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    Informations sur votre pompe à chaleur
                  </h4>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de PAC *
                    </label>
                    <select
                      name="typePac"
                      value={formData.typePac}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Sélectionnez le type</option>
                      <option value="Air/Air">Air/Air</option>
                      <option value="Air/Eau">Air/Eau</option>
                      <option value="Eau/Eau">Eau/Eau</option>
                      <option value="Sol/Eau">Sol/Eau</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marque *
                    </label>
                    <input
                      type="text"
                      name="marquePac"
                      value={formData.marquePac}
                      onChange={handleInputChange}
                      required
                      placeholder="Ex: Daikin, Mitsubishi, Atlantic..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Année d'installation *
                  </label>
                  <input
                    type="number"
                    name="anneePac"
                    value={formData.anneePac}
                    onChange={handleInputChange}
                    required
                    min="2000"
                    max="2024"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commentaires ou demandes particulières
                  </label>
                  <textarea
                    name="commentaires"
                    value={formData.commentaires}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Informations complémentaires..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum 500 caractères ({formData.commentaires.length}/500)
                  </p>
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Souscrire au contrat'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ContratEntretienPage;
